/**
 * Filter navigation items based on user role
 * @param {Array} navData - The full navigation data array
 * @param {string} userRole - The current user's role (e.g., 'ADMIN', 'FACILITY_OWNER', 'MANAGER')
 * @returns {Array} Filtered navigation data
 */
export function filterNavByRole(navData, userRole) {
    if (!navData || !userRole) return navData;

    const normalizedRole = userRole.toUpperCase();

    // If user is ADMIN, return all items
    if (normalizedRole === 'ADMIN') {
        return navData;
    }

    return navData
        .map((section) => {
            // Filter items within each section
            const filteredItems = section.items
                .filter((item) => {
                    // If no roles specified, item is accessible to all
                    if (!item.roles || item.roles.length === 0) {
                        return true;
                    }
                    // Check if user's role is in the allowed roles
                    return item.roles.includes(normalizedRole);
                })
                .map((item) => {
                    // If item has children, filter them too
                    if (item.children && item.children.length > 0) {
                        const filteredChildren = item.children.filter((child) => {
                            // If no roles specified on child, inherit from parent
                            if (!child.roles || child.roles.length === 0) {
                                return true;
                            }
                            return child.roles.includes(normalizedRole);
                        });

                        return {
                            ...item,
                            children: filteredChildren,
                        };
                    }
                    return item;
                });

            // Only include sections that have items after filtering
            if (filteredItems.length > 0) {
                return {
                    ...section,
                    items: filteredItems,
                };
            }

            return null;
        })
        .filter(Boolean); // Remove null sections
}

/**
 * Check if a specific nav item is accessible to a role
 * @param {Object} navItem - The navigation item
 * @param {string} userRole - The user's role
 * @returns {boolean}
 */
export function isNavItemAccessible(navItem, userRole) {
    if (!userRole) return false;

    const normalizedRole = userRole.toUpperCase();

    // Admin has access to everything
    if (normalizedRole === 'ADMIN') {
        return true;
    }

    // If no roles specified, accessible to all
    if (!navItem.roles || navItem.roles.length === 0) {
        return true;
    }

    return navItem.roles.includes(normalizedRole);
}
